/**
 * dial-log-client.ts
 *
 * Supabase write helpers for the activity_log table.
 *
 * IMPORTANT: These functions reference the activity_log table which does not
 * exist in Supabase yet. Migration SQL is at:
 *   /home/user/workspace/dial-and-log-migration.sql
 *
 * All calls are wrapped in try/catch so the UI never hard-crashes while the
 * table is pending. A console.warn fires so the developer knows the write was
 * skipped. Once the migration is applied, remove the stubbed guard.
 *
 * Usage:
 *   await logActivity({ tenant_id, account_id, contact_id, user_id, channel, action, attempt_number });
 */

import { supabase } from "@/lib/session";
import type { ActivityLogInsert, ActivityLogRow } from "./types";

// Guard: set to true after Bryan applies the migration and confirms.
// Until then, all writes are no-ops that log a console.warn.
const MIGRATION_APPLIED = true;

// ─── Write a single activity_log row ─────────────────────────────────────────

export async function logActivity(
  row: ActivityLogInsert
): Promise<ActivityLogRow | null> {
  if (!MIGRATION_APPLIED) {
    console.warn(
      "[dial-log] activity_log table not yet created — skipping write.",
      row
    );
    // Return a fake row so callers can still update local UI state
    return {
      id: crypto.randomUUID(),
      ...row,
      created_at: new Date().toISOString(),
    };
  }

  try {
    const { data, error } = await supabase()
      .from("activity_log")
      .insert(row)
      .select()
      .single();

    if (error) {
      console.error("[dial-log] insert failed:", error.message);
      return null;
    }
    return data as ActivityLogRow;
  } catch (e: any) {
    console.error("[dial-log] unexpected error:", e?.message);
    return null;
  }
}

// ─── Fetch recent activity for a contact ─────────────────────────────────────

export async function fetchContactActivity(
  contactId: number,
  limit = 20
): Promise<ActivityLogRow[]> {
  if (!MIGRATION_APPLIED) {
    console.warn(
      "[dial-log] activity_log table not yet created — returning empty array.",
    );
    return [];
  }

  try {
    const { data, error } = await supabase()
      .from("activity_log")
      .select("*")
      .eq("contact_id", contactId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[dial-log] fetch failed:", error.message);
      return [];
    }
    return (data ?? []) as ActivityLogRow[];
  } catch {
    return [];
  }
}

// ─── Count dial attempts for a contact ───────────────────────────────────────

export async function countDialAttempts(contactId: number): Promise<number> {
  if (!MIGRATION_APPLIED) return 0;

  try {
    const { count, error } = await supabase()
      .from("activity_log")
      .select("*", { count: "exact", head: true })
      .eq("contact_id", contactId)
      .eq("channel", "phone");

    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}
